import { Transition } from "@headlessui/react";
import { CircleDot, Zap, Layers, Globe } from "lucide-react";
import { panelClasses } from "../utils/constants";

// Underlying Lucide components, exported so callers (e.g. the thread renderer)
// can render them DIRECTLY rather than going through the AgentIcon wrapper.
// Rendering the component directly keeps the icon visible at its parent's
// tree depth in React DevTools.
//
// MAIN (Hybrid)   → Zap     — fast, electric, light-touch
// DISCOVERY        → Layers  — stacked depth; carries Discovery's identity
//                              across selector trigger, dropdown row, and
//                              every thread message it produced.
// WWWSEARCH        → Globe
// Unknown / missing → CircleDot fallback.
export const AGENT_ICON_COMPONENT = {
  MAIN: Zap,
  DISCOVERY: Layers,
  WWWSEARCH: Globe,
};

export const AgentIcon = ({
  agent,
  size = 20,
  strokeWidth = 1.5,
  className = "",
}) => {
  const Icon = AGENT_ICON_COMPONENT[agent] || CircleDot;
  return <Icon size={size} strokeWidth={strokeWidth} className={className} />;
};

const DropDownAgentSelector = (props) => {
  const handleToggle = () => {
    props.setIsAgentSelectorOpen(!props.isAgentSelectorOpen);
    props.setIsDropDownNotesOpen(false);
    props.setIsPromptLoaderOpen(false);
  };

  const apiName = props.selectedAgent?.apiName;

  return (
    <div>
      <button
        onClick={handleToggle}
        className="inline-flex items-center gap-1.5 py-1 text-mutedText hover:text-mainText transition-colors font-light cursor-pointer"
      >
        <AgentIcon agent={apiName} size={16} className="text-velBlue" />
        <span className="text-[13px]">{props.selectedAgent?.name}</span>
      </button>
      <Transition
        show={props.isAgentSelectorOpen}
        enter="transition duration-150 ease-out"
        enterFrom="opacity-0 translate-y-2"
        enterTo="opacity-100 translate-y-0"
        leave="transition duration-100 ease-in"
        leaveFrom="opacity-100 translate-y-0"
        leaveTo="opacity-0 translate-y-1"
      >
        <div className="absolute left-0 right-0 bottom-full z-[70]">
          {/* Explicit `bg-surface/90 backdrop-blur-md` after panelClasses so
              the WHOLE panel area is uniformly frosted. */}
          <div
            className={[panelClasses, "bg-surface/90 backdrop-blur-md"].join(
              " ",
            )}
          >
            <div className="max-h-[50vh] overflow-auto p-2">
              <ul className="space-y-0.5">
                {props.agents.map((agent) => {
                  const selected =
                    props.selectedAgent?.apiName === agent.apiName;
                  return (
                    <li key={agent.apiName}>
                      <button
                        onClick={() => {
                          props.setSelectedAgent(agent);
                          props.setIsAgentSelectorOpen(false);
                        }}
                        className={[
                          "group flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors cursor-pointer",
                          selected ? "bg-velBlue/10" : "hover:bg-btnHover",
                        ].join(" ")}
                      >
                        <span className="shrink-0 h-10 w-10 rounded-full bg-velBlue/10 border border-velBlue/20 flex items-center justify-center">
                          <AgentIcon
                            agent={agent.apiName}
                            size={20}
                            className="text-velBlue"
                          />
                        </span>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <p className="truncate text-[13px] font-light text-mainText">
                              {agent.name}
                            </p>
                            {selected && (
                              <span className="rounded-full bg-velBlue/15 px-2 py-px text-[10px] font-light text-velBlue uppercase tracking-wider">
                                Active
                              </span>
                            )}
                          </div>
                          <p className="mt-0.5 text-[12px] font-light text-mutedText line-clamp-1">
                            {agent.description}
                          </p>
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        </div>
      </Transition>
    </div>
  );
};

export default DropDownAgentSelector;
